/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { NodesApi } from '@alfresco/js-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { OcrService } from '../ocr/ocr.service';
import { OcrPipelineService, usableTextLayer } from '../ocr/ocr-pipeline.service';
import { PdfRedactionService } from './pdf-redaction.service';
import { PiiKind, detectPii } from './pii-detector';
import { TextRun, layoutText, overlaps, rectsOf, runsOf } from './pii-layout';
import { PdfStudioService } from './pdf-studio.service';
import { CLICK_KIND, CLICK_TOOLS, PdfAnnot, PdfField, PdfRect, RECT_KIND, RECT_TOOLS, TOOL_INFO, ToolId, annotId } from './pdf-studio.models';
import { MatDialog } from '@angular/material/dialog';
import { PromptDialogComponent, PromptField } from '../shared/prompt-dialog.component';
import { PdfSignature, PdfSigningService, SignatureState } from './pdf-signing.service';
import { SignDialogComponent, SignDialogData, SignDialogResult } from './sign-dialog.component';

type Pdfjs = typeof import('pdfjs-dist');

/** A rendered page and the layers stacked on it. */
interface PageView {
  /** 0-based page index. */
  index: number;
  el: HTMLElement;
  canvas: HTMLCanvasElement;
  overlay: HTMLCanvasElement;
  textEl: HTMLElement;
  viewport: any;
  /** Unscaled page size in points. */
  width: number;
  height: number;
}

/** The floating text editor placed where a click tool lands. */
interface TextPopup {
  page: number;
  /** screen px inside the scroller, for positioning */
  sx: number;
  sy: number;
  /** PDF point the popup is anchored to */
  x: number;
  y: number;
  text: string;
  kind: 'textbox' | 'note' | 'cover';
  /** Set when editing an existing mark instead of placing a new one. */
  editId?: string;
}

const STAMPS = ['DRAFT', 'CONFIDENTIAL', 'APPROVED', 'FINAL'];

/**
 * The professional PDF workbench: page rail, viewer, tools menu and the
 * save-as-new-version button, arranged as the design specifies.
 *
 * Two documents are kept: `viewDoc` (pdf.js) draws the pages and their text
 * layer, and `editDoc` (pdf-lib) carries every structural edit. Page
 * operations are applied to `editDoc` immediately and the viewer reloads from
 * its bytes, so thumbnails and page numbers always tell the truth; drawn
 * marks stay as a live overlay until Save burns them in.
 */
@Component({
  selector: 'app-pdf-studio',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pdf-studio.component.html',
  styleUrl: './pdf-studio.component.scss'
})
export class PdfStudioComponent implements OnInit, AfterViewInit, OnDestroy {
  /** The node being worked on — the parent gates this on PDF mimetype. */
  @Input() nodeId = '';
  @Input() nodeName = '';
  /** Whether the person may write — the save button follows this. */
  @Input() canWrite = false;
  @Output() saved = new EventEmitter<void>();

  @ViewChild('rail', { static: false }) railEl?: ElementRef<HTMLElement>;
  @ViewChild('scroller', { static: false }) scrollerEl?: ElementRef<HTMLElement>;
  @ViewChild('stage', { static: false }) stageEl?: ElementRef<HTMLElement>;
  @ViewChild('signPad', { static: false }) signPadEl?: ElementRef<HTMLCanvasElement>;
  @ViewChild('imagePick', { static: false }) imagePickEl?: ElementRef<HTMLInputElement>;
  @ViewChild('pdfPick', { static: false }) pdfPickEl?: ElementRef<HTMLInputElement>;
  @ViewChild('pdfpop', { static: false }) popupEl?: ElementRef<HTMLElement>;

  private readonly api = inject(AlfrescoApiService);
  private readonly hostEl = inject(ElementRef);
  private readonly studio = inject(PdfStudioService);
  private readonly redaction = inject(PdfRedactionService);
  private readonly ocr = inject(OcrService);
  private readonly pipeline = inject(OcrPipelineService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly signing = inject(PdfSigningService);

  /** The digital signatures in the stored version; null until read, or when they could not be. */
  readonly signatures = signal<PdfSignature[] | null>(null);
  readonly signaturesOpen = signal(false);
  /** A confirmed signature waiting for its mark to be placed on a page. */
  private pendingSign: SignDialogResult | null = null;
  private signerName = '';

  /** The overall state the signature bar shows: the worst of them. */
  readonly signatureSummary = computed<SignatureState | null>(() => {
    const states = (this.signatures() ?? []).map((s) => PdfSigningService.state(s));
    if (!states.length) {
      return null;
    }
    return (['broken', 'unknown', 'later', 'valid'] as SignatureState[]).find((s) => states.includes(s)) ?? null;
  });

  // ---- documents ------------------------------------------------------------
  private pdfjs: Pdfjs | null = null;
  private viewDoc: any = null;
  private editDoc: import('pdf-lib').PDFDocument | null = null;
  private originalBytes: Uint8Array | null = null;

  // ---- view state -----------------------------------------------------------
  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly busy = signal(false);
  readonly dirty = signal(false);
  readonly pageCount = signal(0);
  readonly currentPage = signal(1);
  readonly zoom = signal(1);
  readonly tool = signal<ToolId>('select');
  readonly toolsOpen = signal(false);
  readonly railOpen = signal(true);
  readonly annots = signal<PdfAnnot[]>([]);
  readonly fields = signal<PdfField[]>([]);
  readonly selectedAnnot = signal<PdfAnnot | null>(null);
  readonly selectedField = signal<PdfField | null>(null);

  /** Text search over the text layer. */
  searchOpen = false;
  searchTerm = '';
  readonly searchHits = signal<{ page: number; rect: PdfRect }[]>([]);
  readonly searchIndex = signal(0);

  /** The click-placed text editor. */
  readonly popup = signal<TextPopup | null>(null);
  /** The signature pad. */
  readonly signPadOpen = signal(false);
  private signatureImage: { bytes: Uint8Array; w: number; h: number } | null = null;
  /** The stamp text the stamp submenu last armed. */
  private stampText = STAMPS[0];
  /** The rect an image tool is waiting for its file to fill. */
  private pendingImageRect: { page: number; rect: PdfRect; replace: boolean } | null = null;
  /** Which page a replace/extract acts on — the current page. */
  private pendingReplacePage = 0;

  private pages: PageView[] = [];
  private scrollListener?: () => void;

  /** Undo history — one snapshot per edit, capped. Redo lives in `future`. */
  private past: { annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array }[] = [];
  private future: { annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array }[] = [];
  readonly canUndo = signal(false);
  readonly canRedo = signal(false);

  readonly stamps = STAMPS;

  ngOnInit(): void {
    void this.load();
  }

  ngAfterViewInit(): void {
    const scroller = this.scrollerEl?.nativeElement;
    if (scroller) {
      const onScroll = () => this.trackCurrentPage();
      scroller.addEventListener('scroll', onScroll, { passive: true });
      this.scrollListener = () => scroller.removeEventListener('scroll', onScroll);
    }
    // No ResizeObserver — pages render at an absolute zoom, so container size
    // never drives a repaint, and observing anything inside the scroll area
    // loops: a repaint shows/hides the scrollbar, which resizes the area.
  }

  ngOnDestroy(): void {
    this.scrollListener?.();
    void this.viewDoc?.destroy();
  }

  /** Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y — the usual undo chord, skipped while typing. */
  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && (this.selectedAnnot() || this.selectedField())) {
      e.preventDefault();
      this.deleteSelected();
      return;
    }
    if (!(e.ctrlKey || e.metaKey)) {
      return;
    }
    const key = e.key.toLowerCase();
    if (key === 'z') {
      e.preventDefault();
      void (e.shiftKey ? this.redo() : this.undo());
    } else if (key === 'y') {
      e.preventDefault();
      void this.redo();
    }
  }

  // ---- loading ---------------------------------------------------------------

  private async load(): Promise<void> {
    this.loading.set(true);
    this.failed.set(false);
    try {
      const blob = (await new NodesApi(this.api.getInstance() as any).getNodeContent(this.nodeId)) as unknown as Blob;
      this.originalBytes = new Uint8Array(await blob.arrayBuffer());
      this.editDoc = await this.studio.loadDoc(this.originalBytes);
      this.pdfjs = await import('pdfjs-dist');
      this.pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${this.pdfjs.version}`;
      await this.loadViewer(this.originalBytes);
    } catch {
      this.failed.set(true);
    } finally {
      this.loading.set(false);
    }
    this.loadSignatures();
  }

  private loadSignatures(): void {
    if (this.nodeId) {
      this.signing.signatures(this.nodeId).subscribe((signatures) => this.signatures.set(signatures));
    }
  }

  stateOf(signature: PdfSignature): SignatureState {
    return PdfSigningService.state(signature);
  }

  // ---- digital signature ------------------------------------------------------------

  /**
   * Signs the document with the organisation's key, as the signed-in person.
   *
   * The signature is made by the jobs service over the stored version, so
   * unsaved edits are saved first. A visible mark is optional: placing one
   * means rewriting the file, which an earlier signature would not survive, so
   * once a document is signed later signers sign without one.
   */
  async startDigitalSign(): Promise<void> {
    this.toolsOpen.set(false);
    if (!this.canWrite) {
      this.notification.showError(this.translate.instant('DMS.PDFTOOLS.DS_NO_WRITE'));
      return;
    }
    const existing = (this.signatures() ?? []).length;
    if (existing > 0 && this.dirty()) {
      this.notification.showWarning(this.translate.instant('DMS.PDFTOOLS.DS_SAVE_FIRST'));
      return;
    }
    this.signerName ||= await firstValueFrom(this.signing.me());
    const data: SignDialogData = { signer: this.signerName, dirty: this.dirty(), existing };
    const result = await firstValueFrom(this.dialog.open(SignDialogComponent, { data }).afterClosed());
    if (!result) {
      return;
    }
    if (result.visible) {
      this.pendingSign = result;
      this.tool.set('sign-digital');
      return;
    }
    await this.completeDigitalSign(result);
  }

  /** The mark a visible signature leaves on the page — who, when, why. */
  private signatureMark(result: SignDialogResult): string {
    const when = new Date().toLocaleString(this.translate.currentLang?.startsWith('th') ? 'th-TH' : undefined);
    const lines = [this.translate.instant('DMS.PDFTOOLS.DS_MARK', { p0: this.signerName }), when];
    if (result.reason) {
      lines.push(result.reason);
    }
    return lines.join('\n');
  }

  private async completeDigitalSign(result: SignDialogResult): Promise<void> {
    if (this.dirty() && !(await this.persist(false))) {
      return;
    }
    this.busy.set(true);
    try {
      const signed = await firstValueFrom(
        this.signing.sign(this.nodeId, { reason: result.reason, location: result.location, majorVersion: result.majorVersion })
      );
      this.signatures.set(signed.signatures);
      await this.reloadStored();
      this.saved.emit();
      this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.DS_DONE', { p0: signed.versionLabel ?? '' }));
    } catch (err: any) {
      const reason = err?.error?.message;
      this.notification.showError(this.translate.instant('DMS.PDFTOOLS.DS_FAILED') + (reason ? ` — ${reason}` : ''));
    } finally {
      this.busy.set(false);
    }
  }

  /** Reads the stored version again — after signing, the bytes on the server are what the studio must show and edit. */
  private async reloadStored(): Promise<void> {
    const blob = (await new NodesApi(this.api.getInstance() as any).getNodeContent(this.nodeId)) as unknown as Blob;
    this.originalBytes = new Uint8Array(await blob.arrayBuffer());
    this.editDoc = await this.studio.loadDoc(this.originalBytes);
    this.annots.set([]);
    this.fields.set([]);
    this.clearHistory();
    this.dirty.set(false);
    await this.loadViewer(this.originalBytes);
  }

  /** Rebuilds the pdf.js view from edited bytes — called after every page op. */
  private async loadViewer(bytes: Uint8Array): Promise<void> {
    await this.viewDoc?.destroy();
    if (!this.pdfjs) {
      return;
    }
    this.viewDoc = await this.pdfjs.getDocument({ data: bytes.slice() }).promise;
    this.pageCount.set(this.viewDoc.numPages);
    this.currentPage.set(Math.min(this.currentPage(), this.viewDoc.numPages));
    await this.renderAll();
  }

  // ---- rendering ---------------------------------------------------------------

  /** True while a render pass is in flight — overlapping calls coalesce into one more pass. */
  private rendering = false;
  private renderQueued = false;

  /** Draws every page, its text layer and its annotation layer. */
  private async renderAll(): Promise<void> {
    const stage = this.stageEl?.nativeElement;
    if (!this.viewDoc || !stage) {
      return;
    }
    if (this.rendering) {
      this.renderQueued = true;
      return;
    }
    this.rendering = true;
    try {
      await this.renderPass(stage);
    } catch {
      // a page op swapped viewDoc mid-render — the queued pass repaints
    } finally {
      this.rendering = false;
      if (this.renderQueued) {
        this.renderQueued = false;
        await this.renderAll();
      }
    }
  }

  private async renderPass(stage: HTMLElement): Promise<void> {
    stage.innerHTML = '';
    this.pages = [];

    for (let i = 0; i < this.viewDoc.numPages; i++) {
      const page = await this.viewDoc.getPage(i + 1);
      const natural = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: this.zoom() });

      const el = document.createElement('div');
      el.className = 'aca-pdfp';
      el.style.width = `${viewport.width}px`;
      el.style.height = `${viewport.height}px`;

      const canvas = document.createElement('canvas');
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.ceil(viewport.width * ratio);
      canvas.height = Math.ceil(viewport.height * ratio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        await page.render({ canvasContext: ctx, viewport: page.getViewport({ scale: this.zoom() * ratio }) }).promise;
      }

      const textEl = document.createElement('div');
      textEl.className = 'aca-pdfp__text';
      // pdf.js v5 positions text spans via calc(var(--total-scale-factor) * Npx)
      textEl.style.setProperty('--scale-factor', String(this.zoom()));
      textEl.style.setProperty('--total-scale-factor', String(this.zoom()));
      const overlay = document.createElement('canvas');
      overlay.className = 'aca-pdfp__overlay';
      overlay.width = Math.ceil(viewport.width * ratio);
      overlay.height = Math.ceil(viewport.height * ratio);
      overlay.style.width = `${viewport.width}px`;
      overlay.style.height = `${viewport.height}px`;

      el.append(canvas, textEl, overlay);
      stage.appendChild(el);

      const view: PageView = { index: i, el, canvas, overlay, textEl, viewport, width: natural.width, height: natural.height };
      this.pages.push(view);
      this.bindPointer(view);
      this.drawOverlay(view);
      void this.renderTextLayer(page, view);
      page.cleanup();
    }

    void this.renderThumbs();
  }

  /** The selectable text layer pdf.js paints over the page. */
  private async renderTextLayer(page: any, view: PageView): Promise<void> {
    const pdfjs = this.pdfjs as any;
    view.textEl.innerHTML = '';
    try {
      if (pdfjs.TextLayer) {
        await new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: view.textEl, viewport: view.viewport }).render();
      } else if (pdfjs.renderTextLayer) {
        await pdfjs.renderTextLayer({ textContentSource: page.streamTextContent(), container: view.textEl, viewport: view.viewport }).promise;
      }
    } catch (err) {
      console.warn('[pdf-studio] text layer render failed, using fallback', err);
    }
    if (!view.textEl.childElementCount) {
      await this.buildTextLayer(page, view);
    }
  }

  /** Manual text layer for when pdf.js's TextLayer class can't render a page. */
  private async buildTextLayer(page: any, view: PageView): Promise<void> {
    try {
      const pdfjs = this.pdfjs as any;
      const tc = await page.getTextContent();
      const zoom = this.zoom();
      const spans: { el: HTMLSpanElement; w: number }[] = [];
      for (const item of tc.items) {
        if (!('str' in item) || !item.str) {
          continue;
        }
        const tx = pdfjs.Util.transform(view.viewport.transform, item.transform);
        const fontHeight = Math.hypot(tx[2], tx[3]);
        const span = document.createElement('span');
        span.textContent = item.str;
        span.style.left = `${tx[4]}px`;
        span.style.top = `${tx[5] - fontHeight}px`;
        span.style.fontSize = `${fontHeight}px`;
        span.style.fontFamily = 'sans-serif';
        span.style.transformOrigin = '0 0';
        view.textEl.appendChild(span);
        spans.push({ el: span, w: item.width * zoom });
      }
      // fit every span onto its true width so selection boxes hug the glyphs
      for (const s of spans) {
        const w = s.el.getBoundingClientRect().width;
        if (w > 0) {
          s.el.style.transform = `scaleX(${s.w / w})`;
        }
      }
    } catch (err) {
      console.warn('[pdf-studio] fallback text layer failed', err);
    }
  }

  private async renderThumbs(): Promise<void> {
    const rail = this.railEl?.nativeElement;
    if (!rail || !this.viewDoc) {
      return;
    }
    rail.innerHTML = '';
    for (let i = 0; i < this.viewDoc.numPages; i++) {
      const page = await this.viewDoc.getPage(i + 1);
      const natural = page.getViewport({ scale: 1 });
      const scale = 110 / natural.width;
      const viewport = page.getViewport({ scale });

      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'aca-pdfthumb';
      item.dataset['index'] = String(i);
      item.draggable = true;

      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
      const num = document.createElement('span');
      num.className = 'aca-pdfthumb__num';
      num.textContent = String(i + 1);
      item.append(canvas, num);
      item.addEventListener('click', () => this.goToPage(i + 1));
      item.addEventListener('dragstart', (e) => e.dataTransfer?.setData('text/plain', String(i)));
      item.addEventListener('dragover', (e) => e.preventDefault());
      item.addEventListener('drop', (e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer?.getData('text/plain'));
        void this.movePage(from, i);
      });
      rail.appendChild(item);
      page.cleanup();
    }
    this.markCurrentThumb();
  }

  // ---- zoom / pages ------------------------------------------------------------

  setZoom(value: number): void {
    this.zoom.set(Math.min(4, Math.max(0.25, value)));
    void this.renderAll();
  }

  zoomIn(): void {
    this.setZoom(this.zoom() + 0.25);
  }

  zoomOut(): void {
    this.setZoom(this.zoom() - 0.25);
  }

  async fit(widthOnly: boolean): Promise<void> {
    const scroller = this.scrollerEl?.nativeElement;
    if (!scroller || !this.viewDoc) {
      return;
    }
    const page = await this.viewDoc.getPage(this.currentPage());
    const natural = page.getViewport({ scale: 1 });
    const scale = widthOnly
      ? (scroller.clientWidth - 48) / natural.width
      : Math.min((scroller.clientWidth - 48) / natural.width, (scroller.clientHeight - 48) / natural.height);
    this.setZoom(scale);
  }

  goToPage(n: number): void {
    const target = this.pages[Math.max(0, Math.min(this.pages.length - 1, n - 1))];
    target?.el.scrollIntoView({ block: 'start' });
    this.currentPage.set(Math.max(1, Math.min(this.pageCount(), n)));
    this.markCurrentThumb();
  }

  nextPage(d: number): void {
    this.goToPage(this.currentPage() + d);
  }

  private trackCurrentPage(): void {
    const scroller = this.scrollerEl?.nativeElement;
    if (!scroller) {
      return;
    }
    const top = scroller.scrollTop + scroller.clientHeight / 3;
    const idx = this.pages.findIndex((p) => p.el.offsetTop + p.el.offsetHeight > top);
    const page = (idx === -1 ? this.pages.length : idx + 1) || 1;
    if (page !== this.currentPage()) {
      this.currentPage.set(page);
      this.markCurrentThumb();
    }
  }

  private markCurrentThumb(): void {
    this.railEl?.nativeElement
      .querySelectorAll('.aca-pdfthumb')
      .forEach((el, i) => el.classList.toggle('aca-pdfthumb--on', i === this.currentPage() - 1));
  }

  // ---- tools -----------------------------------------------------------------

  arm(tool: ToolId): void {
    // picking another tool while a signature waits for its mark gives up on that signature
    if (tool !== 'sign-digital') {
      this.pendingSign = null;
    }
    this.tool.set(tool);
    this.toolsOpen.set(false);
    this.selectedAnnot.set(null);
    this.selectedField.set(null);
    this.popup.set(null);
    if (tool === 'sign' && !this.signatureImage) {
      this.openSignPad();
    }
  }

  armStamp(text: string): void {
    this.stampText = text;
    this.arm('stamp');
  }

  /** True while the document is being searched for personal data. */
  readonly scanning = signal(false);

  /**
   * Looks through the text of every page for personal data — Thai national IDs,
   * card numbers, phone numbers, emails, bank accounts — and marks each one for
   * covering. It only marks: each box can be looked at, moved or deleted before
   * the file is saved. The matching rules run in this browser and no AI is
   * involved; a scanned page, which has no text to match against, is read by the
   * OCR engine on the server first.
   *
   * A page that OCR cannot read either is counted so the person knows to mark
   * it by hand.
   */
  async findPersonalData(): Promise<void> {
    if (!this.viewDoc || this.scanning()) {
      return;
    }

    this.scanning.set(true);
    try {
      const marks: PdfAnnot[] = [];
      const counts: Partial<Record<PiiKind, number>> = {};
      let withoutText = 0;
      let readByOcr = 0;

      for (let i = 0; i < this.viewDoc.numPages; i++) {
        const page = await this.viewDoc.getPage(i + 1);
        try {
          const content = await page.getTextContent();
          let layout = layoutText(runsOf(content.items));
          if (!usableTextLayer(layout.text)) {
            const scanned = await this.ocrPageText(page, i + 1);
            if (!scanned) {
              withoutText++;
              continue;
            }
            layout = scanned;
            readByOcr++;
          }

          const taken = this.annots().filter((a) => a.kind === 'redact' && a.page === i);
          for (const match of detectPii(layout.text)) {
            const boxes = rectsOf(layout, match.start, match.end).filter((box) => !taken.some((t) => overlaps(t, box)));
            if (boxes.length) {
              counts[match.kind] = (counts[match.kind] ?? 0) + 1;
            }
            for (const box of boxes) {
              marks.push({ id: annotId(), page: i, kind: 'redact', ...box });
            }
          }
        } finally {
          page.cleanup();
        }
      }

      if (marks.length) {
        void this.pushHistory();
        this.annots.update((list) => [...list, ...marks]);
        this.dirty.set(true);
        this.pages.forEach((p) => this.drawOverlay(p));
      }
      this.toolsOpen.set(false);
      this.reportScan(counts, withoutText, readByOcr);
    } catch {
      this.notification.showError(this.translate.instant('DMS.PDFTOOLS.PII_FAILED'));
    } finally {
      this.scanning.set(false);
    }
  }

  private reportScan(counts: Partial<Record<PiiKind, number>>, withoutText: number, readByOcr: number): void {
    const found = Object.entries(counts) as [PiiKind, number][];
    const total = found.reduce((sum, [, n]) => sum + n, 0);
    const kinds = found.map(([kind, n]) => `${this.translate.instant('DMS.PDFTOOLS.PII_' + kind.toUpperCase())} ${n}`).join(', ');

    if (total) {
      this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.PII_FOUND', { count: total, kinds }));
    } else {
      this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.PII_NONE'));
    }
    if (readByOcr) {
      this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.PII_SCANNED', { pages: readByOcr }));
    }
    if (withoutText) {
      this.notification.showWarning(this.translate.instant('DMS.PDFTOOLS.PII_NO_TEXT', { pages: withoutText }));
    }
  }

  /**
   * Reads a page that has no usable text of its own — a scan. The page is drawn
   * and sent to the OCR engine, whose words come back with pixel boxes; those
   * are turned into text runs in PDF points so the rules above can mark what
   * they find. The read text is also put into the search index, which the page
   * never had before.
   */
  private async ocrPageText(page: any, pageNumber: number): Promise<ReturnType<typeof layoutText> | null> {
    const natural = page.getViewport({ scale: 1 });
    const scale = Math.min(3, Math.max(1, 2000 / Math.max(natural.width, natural.height)));
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    const image = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    canvas.width = 0;
    canvas.height = 0;
    if (!image) {
      return null;
    }

    const file = new File([image], `${this.nodeName}-${pageNumber}.png`, { type: 'image/png' });
    const result = await firstValueFrom(this.ocr.recognize(file)).catch(() => null);
    if (!result?.lines?.length) {
      return null;
    }

    if (this.nodeId) {
      void this.ocr.indexOcrText(this.nodeId, this.nodeName, result, pageNumber).subscribe({ error: () => undefined });
    }

    // a word's box is image pixels, counted from the top; PDF points count from the bottom
    const runs: TextRun[] = [];
    for (const line of result.lines) {
      const words = line.words?.length ? line.words : [{ text: line.text, box: line.box }];
      words.forEach((word, index) => {
        const xs = [word.box[0], word.box[2], word.box[4], word.box[6]];
        const ys = [word.box[1], word.box[3], word.box[5], word.box[7]];
        const left = Math.min(...xs) / scale;
        const right = Math.max(...xs) / scale;
        const top = Math.min(...ys) / scale;
        const bottom = Math.max(...ys) / scale;
        runs.push({
          str: word.text,
          x: left,
          y: natural.height - bottom,
          w: right - left,
          h: bottom - top,
          eol: index === words.length - 1
        });
      });
    }
    return runs.length ? layoutText(runs) : null;
  }

  /** The marked areas are committed when the file is saved — remind the person. */
  applyRedact(): void {
    this.toolsOpen.set(false);
    this.tool.set('select');
    this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.R_NOTE'));
  }

  isTool(...tools: ToolId[]): boolean {
    return tools.includes(this.tool());
  }

  /** The armed-tool chip shown over the viewer while a placement tool is active. */
  readonly armedTool = computed(() => {
    const info = TOOL_INFO[this.tool()];
    if (!info) {
      return null;
    }
    return {
      icon: info.icon,
      label: this.translate.instant(`DMS.PDFTOOLS.${info.label}`),
      hint: this.translate.instant(`DMS.PDFTOOLS.HINT_${info.hint.toUpperCase()}`)
    };
  });

  // ---- pointer handling ---------------------------------------------------------

  private drag: { startX: number; startY: number; view: PageView; ink?: { x: number; y: number }[] } | null = null;
  /** A select-tool drag that is moving a mark or a field. */
  private moveDrag: { id: string; field: boolean; lastX: number; lastY: number; view: PageView; recorded: boolean } | null = null;

  private bindPointer(view: PageView): void {
    view.overlay.addEventListener('pointerdown', (e) => this.pointerDown(e, view));
    view.overlay.addEventListener('pointermove', (e) => this.pointerMove(e, view));
    view.overlay.addEventListener('pointerup', (e) => this.pointerUp(e, view));
    view.overlay.addEventListener('dblclick', (e) => this.doubleClick(e, view));
    // a dragged text-layer selection leaves a smearing drag ghost — never allow it
    view.el.addEventListener('dragstart', (e) => e.preventDefault());
    view.el.addEventListener('mouseup', () => this.markupUp(view));
  }

  private toPdf(view: PageView, e: PointerEvent): { x: number; y: number } {
    const box = view.el.getBoundingClientRect();
    const [x, y] = view.viewport.convertToPdfPoint(e.clientX - box.left, e.clientY - box.top);
    return { x, y };
  }

  private pointerDown(e: PointerEvent, view: PageView): void {
    const t = this.tool();
    if (t === 'pan') {
      const scroller = this.scrollerEl?.nativeElement;
      if (!scroller) {
        return;
      }
      const startX = e.clientX;
      const startY = e.clientY;
      const startL = scroller.scrollLeft;
      const startT = scroller.scrollTop;
      const move = (ev: PointerEvent) => {
        scroller.scrollLeft = startL - (ev.clientX - startX);
        scroller.scrollTop = startT - (ev.clientY - startY);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      return;
    }
    if (t === 'ink' || RECT_TOOLS.has(t)) {
      const p = this.toPdf(view, e);
      this.drag = { startX: p.x, startY: p.y, view, ink: t === 'ink' ? [p] : undefined };
      view.overlay.setPointerCapture(e.pointerId);
      e.preventDefault();
      return;
    }
    if (t === 'select') {
      const p = this.toPdf(view, e);
      const annot = this.annots().find((a) => a.page === view.index && this.hit(a, p));
      const field = annot ? undefined : this.fields().find((f) => f.page === view.index && this.hitRect(f.rect, p));
      this.selectedAnnot.set(annot ?? null);
      this.selectedField.set(field ?? null);
      const targetId = annot?.id ?? field?.id;
      e.preventDefault();
      if (targetId) {
        // armed for a possible move — history is recorded on the first real shift
        this.moveDrag = { id: targetId, field: !!field, lastX: p.x, lastY: p.y, view, recorded: false };
        view.overlay.setPointerCapture(e.pointerId);
      }
      this.drawOverlay(view);
      return;
    }
  }

  private pointerMove(e: PointerEvent, view: PageView): void {
    if (this.tool() === 'select' && !this.moveDrag) {
      const p = this.toPdf(view, e);
      const over =
        this.annots().some((a) => a.page === view.index && this.hit(a, p)) ||
        this.fields().some((f) => f.page === view.index && this.hitRect(f.rect, p));
      view.overlay.style.cursor = over ? 'move' : 'default';
      return;
    }
    if (this.moveDrag && this.moveDrag.view === view) {
      const p = this.toPdf(view, e);
      const dx = p.x - this.moveDrag.lastX;
      const dy = p.y - this.moveDrag.lastY;
      if (Math.abs(dx) + Math.abs(dy) < 0.5) {
        return;
      }
      if (!this.moveDrag.recorded) {
        this.moveDrag.recorded = true;
        void this.pushHistory();
      }
      this.moveDrag.lastX = p.x;
      this.moveDrag.lastY = p.y;
      this.moveTarget(this.moveDrag.id, this.moveDrag.field, dx, dy);
      this.drawOverlay(view);
      return;
    }
    if (!this.drag || this.drag.view !== view) {
      return;
    }
    const p = this.toPdf(view, e);
    if (this.drag.ink) {
      this.drag.ink.push(p);
    }
    this.drawOverlay(view, this.previewRect(p));
  }

  /** Shifts the dragged mark or field by a PDF-space delta. */
  private moveTarget(id: string, isField: boolean, dx: number, dy: number): void {
    if (isField) {
      this.fields.update((list) =>
        list.map((f) => (f.id === id ? { ...f, rect: { x: f.rect.x + dx, y: f.rect.y + dy, w: f.rect.w, h: f.rect.h } } : f))
      );
      this.selectedField.update((f) => (f && f.id === id ? { ...f, rect: { x: f.rect.x + dx, y: f.rect.y + dy, w: f.rect.w, h: f.rect.h } } : f));
    } else {
      this.annots.update((list) => list.map((a) => (a.id === id ? offsetAnnot(a, dx, dy) : a)));
      this.selectedAnnot.update((a) => (a && a.id === id ? offsetAnnot(a, dx, dy) : a));
    }
    this.dirty.set(true);
  }

  /** Double-click on a text mark reopens its editor. */
  private doubleClick(e: MouseEvent, view: PageView): void {
    if (this.tool() !== 'select') {
      return;
    }
    const box = view.el.getBoundingClientRect();
    const [x, y] = view.viewport.convertToPdfPoint(e.clientX - box.left, e.clientY - box.top);
    const annot = this.annots().find((a) => a.page === view.index && this.hit(a, { x, y }));
    if (!annot || !['textbox', 'note', 'cover', 'stamp'].includes(annot.kind)) {
      return;
    }
    const hostBox = (this.hostEl.nativeElement as HTMLElement).getBoundingClientRect();
    this.selectedAnnot.set(annot);
    this.openPopup({
      page: view.index,
      sx: e.clientX - hostBox.left,
      sy: e.clientY - hostBox.top,
      x: annot.x,
      y: annot.y,
      text: annot.text ?? '',
      kind: annot.kind === 'stamp' ? 'textbox' : (annot.kind as TextPopup['kind']),
      editId: annot.id
    });
  }

  private pointerUp(e: PointerEvent, view: PageView): void {
    const t = this.tool();
    const p = this.toPdf(view, e);

    if (this.moveDrag) {
      this.moveDrag = null;
      return;
    }

    if (this.drag) {
      const { startX, startY } = this.drag;
      if (this.drag.ink && this.drag.ink.length > 1) {
        this.addAnnot({ id: annotId(), page: view.index, kind: 'ink', x: 0, y: 0, w: 0, h: 0, points: this.drag.ink, color: '#d32f2f' });
      } else if (!this.drag.ink) {
        const rect = normRect(startX, startY, p.x, p.y);
        if (rect.w > 2 && rect.h > 2) {
          this.rectDone(view.index, rect);
        } else if (t.startsWith('field-')) {
          // a plain click drops a standard-size field centred on the click
          this.rectDone(view.index, defaultFieldRect(t, p.x, p.y));
        }
      }
      this.drag = null;
      this.drawOverlay(view);
      return;
    }

    if (CLICK_TOOLS.has(t)) {
      this.clickTool(view, p, e);
    }
  }

  /** A selection-based markup (highlight / underline / strike) lands on mouseup. */
  private markupUp(view: PageView): void {
    const t = this.tool();
    if (t !== 'highlight' && t !== 'underline' && t !== 'strike') {
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      return;
    }
    const box = view.el.getBoundingClientRect();
    const rects: PdfRect[] = [];
    for (let i = 0; i < sel.rangeCount; i++) {
      for (const r of Array.from(sel.getRangeAt(i).getClientRects())) {
        if (r.width < 2 || r.bottom < box.top || r.top > box.bottom) {
          continue;
        }
        // pdf.js v5 has no convertToPdfRectangle — convert the two corners
        const [x1, yBottom] = view.viewport.convertToPdfPoint(r.left - box.left, r.bottom - box.top);
        const [x2, yTop] = view.viewport.convertToPdfPoint(r.right - box.left, r.top - box.top);
        rects.push({ x: x1, y: yBottom, w: x2 - x1, h: yTop - yBottom });
      }
    }
    sel.removeAllRanges();
    if (rects.length) {
      this.addAnnot({ id: annotId(), page: view.index, kind: t, x: 0, y: 0, w: 0, h: 0, rects });
      this.tool.set('select');
    } else {
      console.warn('[pdf-studio] markup produced no in-page rects — text layer may be misplaced');
    }
  }

  /** The rectangle a drag produced — annotate, crop, place image or field. */
  private rectDone(page: number, rect: PdfRect): void {
    const t = this.tool();
    const kind = RECT_KIND[t];
    if (kind) {
      const color = t === 'redact' ? '#000000' : '#d32f2f';
      const a: PdfAnnot = { id: annotId(), page, kind, ...rect, color };
      if (kind === 'redact') {
        a.color = '#e53935';
      }
      this.addAnnot(a);
      if (t !== 'redact') {
        this.tool.set('select');
      }
      return;
    }
    if (t === 'crop') {
      void this.cropPage(page, rect);
      this.tool.set('select');
      return;
    }
    if (t === 'add-image' || t === 'edit-image') {
      this.pendingImageRect = { page, rect, replace: t === 'edit-image' };
      this.imagePickEl?.nativeElement.click();
      return;
    }
    if (t === 'field-text' || t === 'field-check' || t === 'field-radio' || t === 'field-dropdown') {
      this.placeField(page, rect, t);
      this.tool.set('select');
    }
  }

  private placeField(page: number, rect: PdfRect, tool: ToolId): void {
    const n = this.fields().length + 1;
    const add = (field: PdfField) => {
      void this.pushHistory();
      this.fields.update((f) => [...f, field]);
      this.dirty.set(true);
    };
    if (tool === 'field-text') {
      add({ id: annotId(), page, kind: 'text', name: `field_${n}`, rect });
    } else if (tool === 'field-check') {
      add({ id: annotId(), page, kind: 'check', name: `check_${n}`, rect });
    } else if (tool === 'field-radio') {
      // a cancelled dialog places nothing, and leaves no step to undo
      this.ask('DMS.PDFTOOLS.RADIO_GROUP', [
        { key: 'group', label: 'DMS.PDFTOOLS.RADIO_GROUP', value: 'group1', required: true },
        { key: 'option', label: 'DMS.PDFTOOLS.RADIO_OPTION', value: `option${n}`, required: true }
      ]).subscribe((v) => v && add({ id: annotId(), page, kind: 'radio', name: v['group'], rect, options: [v['option']] }));
    } else {
      this.ask('DMS.PDFTOOLS.DROPDOWN_OPTIONS', [{ key: 'csv', label: 'DMS.PDFTOOLS.DROPDOWN_OPTIONS', value: 'A,B,C', required: true }]).subscribe(
        (v) => {
          const options = (v?.['csv'] ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          if (options.length) {
            add({ id: annotId(), page, kind: 'dropdown', name: `choice_${n}`, rect, options });
          }
        }
      );
    }
  }

  private ask(title: string, fields: PromptField[]): Observable<Record<string, string> | undefined> {
    return this.dialog.open(PromptDialogComponent, { data: { title, fields } }).afterClosed();
  }

  /** The single-click tools — text, note, stamp, signature. */
  private clickTool(view: PageView, p: { x: number; y: number }, e: PointerEvent): void {
    const t = this.tool();
    const hostBox = (this.hostEl.nativeElement as HTMLElement).getBoundingClientRect();

    if (t === 'stamp') {
      const w = 120;
      const h = 36;
      this.addAnnot({ id: annotId(), page: view.index, kind: 'stamp', x: p.x - w / 2, y: p.y - h / 2, w, h, text: this.stampText, color: '#c62828' });
      this.tool.set('select');
      return;
    }
    if (t === 'sign' && this.signatureImage) {
      const w = Math.min(160, this.signatureImage.w);
      const h = (this.signatureImage.h / this.signatureImage.w) * w;
      this.addAnnot({
        id: annotId(),
        page: view.index,
        kind: 'image',
        x: p.x - w / 2,
        y: p.y - h / 2,
        w,
        h,
        image: { bytes: this.signatureImage.bytes, kind: 'png' }
      });
      this.tool.set('select');
      return;
    }
    if (t === 'sign-field') {
      // a place for a handwritten signature — not a digital one, so it does not say it is
      const w = 170;
      const h = 44;
      this.addAnnot({
        id: annotId(),
        page: view.index,
        kind: 'stamp',
        x: p.x - w / 2,
        y: p.y - h / 2,
        w,
        h,
        text: this.translate.instant('DMS.PDFTOOLS.F_SIGN_MARK'),
        color: '#1565c0'
      });
      this.tool.set('select');
      return;
    }
    if (t === 'sign-digital' && this.pendingSign) {
      const pending = this.pendingSign;
      this.pendingSign = null;
      const w = 190;
      const h = 54;
      this.addAnnot({
        id: annotId(),
        page: view.index,
        kind: 'stamp',
        x: p.x - w / 2,
        y: p.y - h / 2,
        w,
        h,
        text: this.signatureMark(pending),
        color: '#1565c0'
      });
      this.tool.set('select');
      void this.completeDigitalSign(pending);
      return;
    }

    const kind = CLICK_KIND[t];
    if (!kind) {
      return;
    }
    this.openPopup({
      page: view.index,
      sx: e.clientX - hostBox.left,
      sy: e.clientY - hostBox.top,
      x: p.x,
      y: p.y,
      text: '',
      kind: kind as TextPopup['kind']
    });
  }

  /** Opens the floating editor clamped inside the studio, then focuses it. */
  private openPopup(pop: TextPopup): void {
    const host = this.hostEl.nativeElement as HTMLElement;
    const box = host.getBoundingClientRect();
    pop.sx = Math.min(Math.max(8, pop.sx), Math.max(8, box.width - 296));
    pop.sy = Math.min(Math.max(8, pop.sy), Math.max(8, box.height - 210));
    this.popup.set(pop);
    setTimeout(() => this.popupEl?.nativeElement.querySelector('textarea')?.focus());
  }

  popupIcon(pop: TextPopup): string {
    if (pop.editId) {
      return 'edit';
    }
    return pop.kind === 'note' ? 'sticky_note_2' : pop.kind === 'cover' ? 'format_color_fill' : 'text_fields';
  }

  popupTitle(pop: TextPopup): string {
    if (pop.editId) {
      return this.translate.instant('DMS.PDFTOOLS.POPUP_EDIT');
    }
    const key = pop.kind === 'note' ? 'POPUP_NOTE' : pop.kind === 'cover' ? 'POPUP_COVER' : 'POPUP_ADD';
    return this.translate.instant(`DMS.PDFTOOLS.${key}`);
  }

  /** Enter saves, Shift+Enter breaks a line, Escape cancels. */
  popupKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.confirmPopup();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.cancelPopup();
    }
  }

  /** Deletes the mark whose editor is open. */
  deletePopupMark(): void {
    const pop = this.popup();
    if (!pop?.editId) {
      return;
    }
    void this.pushHistory();
    this.annots.update((list) => list.filter((a) => a.id !== pop.editId));
    this.selectedAnnot.set(null);
    this.popup.set(null);
    this.dirty.set(true);
    this.pages.forEach((p) => this.drawOverlay(p));
  }

  confirmPopup(): void {
    const pop = this.popup();
    if (!pop) {
      return;
    }
    if (pop.editId) {
      // editing an existing mark — empty text means remove it
      void this.pushHistory();
      if (!pop.text.trim()) {
        this.annots.update((list) => list.filter((a) => a.id !== pop.editId));
        this.selectedAnnot.set(null);
      } else {
        this.annots.update((list) =>
          list.map((a) => (a.id === pop.editId ? { ...a, text: pop.text.trim(), w: Math.max(40, pop.text.length * 7) } : a))
        );
      }
      this.dirty.set(true);
      this.popup.set(null);
      this.pages.forEach((p) => this.drawOverlay(p));
      return;
    }
    if (!pop.text.trim()) {
      this.popup.set(null);
      return;
    }
    const h = pop.kind === 'cover' ? 18 : 16;
    this.addAnnot({
      id: annotId(),
      page: pop.page,
      kind: pop.kind,
      x: pop.x,
      y: pop.y,
      w: Math.max(80, pop.text.length * 7),
      h,
      text: pop.text,
      color: pop.kind === 'cover' ? '#1a1a1a' : '#1565c0'
    });
    this.popup.set(null);
    this.tool.set('select');
  }

  cancelPopup(): void {
    this.popup.set(null);
  }

  // ---- annotations ---------------------------------------------------------

  /**
   * Remembers the state before a change. Mark-only edits snapshot the annot /
   * field lists; page surgery also snapshots the pdf-lib bytes so undo can
   * restore the whole document.
   */
  private pushHistory(withDoc = false): Promise<void> | void {
    const snap: { annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array } = {
      annots: this.annots(),
      fields: this.fields()
    };
    const done = () => {
      this.past.push(snap);
      if (this.past.length > 40) {
        this.past.shift();
      }
      this.future = [];
      this.canUndo.set(true);
      this.canRedo.set(false);
    };
    if (withDoc && this.editDoc) {
      return this.editDoc.save().then((bytes) => {
        snap.bytes = bytes;
        done();
      });
    }
    done();
  }

  private async currentSnapshot(withDoc: boolean): Promise<{ annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array }> {
    const snap: { annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array } = { annots: this.annots(), fields: this.fields() };
    if (withDoc && this.editDoc) {
      snap.bytes = await this.editDoc.save();
    }
    return snap;
  }

  private async applySnap(snap: { annots: PdfAnnot[]; fields: PdfField[]; bytes?: Uint8Array }): Promise<void> {
    this.annots.set(snap.annots);
    this.fields.set(snap.fields);
    if (snap.bytes) {
      this.editDoc = await this.studio.loadDoc(snap.bytes);
      await this.loadViewer(snap.bytes);
    } else {
      this.pages.forEach((p) => this.drawOverlay(p));
    }
    this.canUndo.set(this.past.length > 0);
    this.canRedo.set(this.future.length > 0);
    this.dirty.set(this.past.length > 0);
  }

  async undo(): Promise<void> {
    const snap = this.past.pop();
    if (!snap) {
      return;
    }
    this.future.push(await this.currentSnapshot(!!snap.bytes));
    await this.applySnap(snap);
  }

  async redo(): Promise<void> {
    const snap = this.future.pop();
    if (!snap) {
      return;
    }
    this.past.push(await this.currentSnapshot(!!snap.bytes));
    await this.applySnap(snap);
    this.canUndo.set(this.past.length > 0);
    this.dirty.set(true);
  }

  private clearHistory(): void {
    this.past = [];
    this.future = [];
    this.canUndo.set(false);
    this.canRedo.set(false);
  }

  private addAnnot(annot: PdfAnnot, record = true): void {
    if (record) {
      void this.pushHistory();
    }
    this.annots.update((list) => [...list, annot]);
    this.dirty.set(true);
    const view = this.pages[annot.page];
    if (view) {
      this.drawOverlay(view);
    }
  }

  deleteSelected(): void {
    const sel = this.selectedAnnot();
    const selField = this.selectedField();
    if (!sel && !selField) {
      return;
    }
    void this.pushHistory();
    if (sel) {
      this.annots.update((list) => list.filter((a) => a.id !== sel.id));
      this.selectedAnnot.set(null);
    }
    if (selField) {
      this.fields.update((list) => list.filter((f) => f.id !== selField.id));
      this.selectedField.set(null);
    }
    this.dirty.set(true);
    this.pages.forEach((p) => this.drawOverlay(p));
  }

  private hit(a: PdfAnnot, p: { x: number; y: number }): boolean {
    if (a.rects?.length) {
      return a.rects.some((r) => this.hitRect(r, p));
    }
    return this.hitRect(annotBounds(a), p);
  }

  private hitRect(r: PdfRect, p: { x: number; y: number }): boolean {
    const pad = 5;
    return p.x >= r.x - pad && p.x <= r.x + Math.max(r.w, 14) + pad && p.y >= r.y - pad && p.y <= r.y + Math.max(r.h, 14) + pad;
  }

  // ---- overlay drawing ------------------------------------------------------

  private previewRect(p: { x: number; y: number }): PdfRect | null {
    if (!this.drag || this.drag.ink) {
      return null;
    }
    return normRect(this.drag.startX, this.drag.startY, p.x, p.y);
  }

  /** Redraws one page's annotation layer at display scale. */
  private drawOverlay(view: PageView, preview?: PdfRect | null): void {
    const ctx = view.overlay.getContext('2d');
    if (!ctx) {
      return;
    }
    const ratio = window.devicePixelRatio || 1;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, view.overlay.width, view.overlay.height);

    const mine = this.annots().filter((a) => a.page === view.index);
    for (const a of mine) {
      this.drawAnnot(ctx, view, a);
    }
    for (const f of this.fields().filter((field) => field.page === view.index)) {
      const r = this.toView(view, f.rect);
      ctx.strokeStyle = '#7b1fa2';
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(123,31,162,0.08)';
      ctx.fillRect(r.x, r.y, r.w, r.h);
    }
    const sel = this.selectedAnnot();
    if (sel && sel.page === view.index) {
      const box = sel.rects ? unionRects(sel.rects) : annotBounds(sel);
      const r = this.toView(view, box);
      ctx.strokeStyle = '#1976d2';
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6);
      ctx.setLineDash([]);
    }
    const selField = this.selectedField();
    if (selField && selField.page === view.index) {
      const r = this.toView(view, selField.rect);
      ctx.strokeStyle = '#e65100';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6);
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
    }
    if (preview) {
      const r = this.toView(view, preview);
      ctx.strokeStyle = this.tool() === 'crop' ? '#1565c0' : this.tool() === 'redact' ? '#000' : '#d32f2f';
      ctx.setLineDash(this.tool() === 'crop' || this.tool() === 'redact' ? [5, 4] : []);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.setLineDash([]);
    }
    this.drawSearchHits(ctx, view);
    if (this.drag?.ink) {
      this.strokeInk(ctx, view, this.drag.ink);
    }
  }

  private toView(view: PageView, r: PdfRect): { x: number; y: number; w: number; h: number } {
    const [x1, y1, x2, y2] = view.viewport.convertToViewportRectangle([r.x, r.y + r.h, r.x + r.w, r.y]);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }

  private drawAnnot(ctx: CanvasRenderingContext2D, view: PageView, a: PdfAnnot): void {
    ctx.save();
    const color = a.color ?? '#e53935';
    switch (a.kind) {
      case 'highlight':
        ctx.fillStyle = 'rgba(255,235,59,0.4)';
        for (const r of a.rects ?? []) {
          const v = this.toView(view, r);
          ctx.fillRect(v.x, v.y, v.w, v.h);
        }
        break;
      case 'underline':
      case 'strike':
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        for (const r of a.rects ?? []) {
          const v = this.toView(view, r);
          const y = a.kind === 'underline' ? v.y + v.h : v.y + v.h / 2;
          ctx.beginPath();
          ctx.moveTo(v.x, y);
          ctx.lineTo(v.x + v.w, y);
          ctx.stroke();
        }
        break;
      case 'ink':
        this.strokeInk(ctx, view, a.points ?? [], color);
        break;
      case 'rect':
      case 'ellipse':
      case 'line':
      case 'arrow':
      case 'redact': {
        const v = this.toView(view, a);
        ctx.strokeStyle = color;
        ctx.fillStyle = a.kind === 'redact' ? 'rgba(229,57,53,0.15)' : 'transparent';
        ctx.lineWidth = 1.5;
        if (a.kind === 'rect' || a.kind === 'redact') {
          if (a.kind === 'redact') {
            ctx.fillRect(v.x, v.y, v.w, v.h);
          } else {
            ctx.strokeRect(v.x, v.y, v.w, v.h);
          }
        } else if (a.kind === 'ellipse') {
          ctx.beginPath();
          ctx.ellipse(v.x + v.w / 2, v.y + v.h / 2, v.w / 2, v.h / 2, 0, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.moveTo(v.x, v.y);
          ctx.lineTo(v.x + v.w, v.y + v.h);
          ctx.stroke();
          if (a.kind === 'arrow') {
            const ang = Math.atan2(v.h, v.w);
            for (const d of [2.6, -2.6]) {
              ctx.beginPath();
              ctx.moveTo(v.x + v.w, v.y + v.h);
              ctx.lineTo(v.x + v.w + 10 * Math.cos(ang + d), v.y + v.h + 10 * Math.sin(ang + d));
              ctx.stroke();
            }
          }
        }
        break;
      }
      case 'cover': {
        const v = this.toView(view, a);
        ctx.fillStyle = '#fff';
        ctx.fillRect(v.x, v.y, v.w, v.h);
        ctx.fillStyle = color;
        ctx.font = `${12 * this.zoom()}px sans-serif`;
        ctx.fillText(a.text ?? '', v.x, v.y + v.h * 0.75);
        break;
      }
      case 'stamp': {
        const v = this.toView(view, a);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(v.x, v.y, v.w, v.h);
        ctx.fillStyle = color;
        ctx.font = `700 ${Math.min(16 * this.zoom(), v.h * 0.5)}px sans-serif`;
        ctx.textAlign = 'center';
        (a.text ?? '')
          .split('\n')
          .forEach((line, i, arr) => ctx.fillText(line, v.x + v.w / 2, v.y + v.h / 2 + (i - (arr.length - 1) / 2) * v.h * 0.4 + v.h * 0.12));
        ctx.textAlign = 'left';
        break;
      }
      case 'note': {
        const v = this.toView(view, { x: a.x, y: a.y, w: 12, h: 12 });
        ctx.fillStyle = '#fdd835';
        ctx.strokeStyle = '#f9a825';
        ctx.fillRect(v.x, v.y, v.w, v.h);
        ctx.strokeRect(v.x, v.y, v.w, v.h);
        if (a.text) {
          ctx.fillStyle = 'rgba(0,0,0,0.6)';
          ctx.font = `${9 * this.zoom()}px sans-serif`;
          ctx.fillText(a.text, v.x + v.w + 4, v.y + v.h);
        }
        break;
      }
      case 'image': {
        const v = this.toView(view, a);
        if (a.image) {
          // cache the decoded element — recreating it per redraw let stale
          // onload callbacks stamp the image at old positions after later clears
          if (!a.image.el) {
            const img = new Image();
            img.onload = () => {
              URL.revokeObjectURL(img.src);
              this.pages.forEach((p) => this.drawOverlay(p));
            };
            img.src = URL.createObjectURL(
              new Blob([a.image.bytes.slice().buffer as ArrayBuffer], { type: `image/${a.image.kind === 'jpg' ? 'jpeg' : 'png'}` })
            );
            a.image.el = img;
          }
          if (a.image.el.complete && a.image.el.naturalWidth > 0) {
            ctx.drawImage(a.image.el, v.x, v.y, v.w, v.h);
          } else {
            ctx.strokeStyle = color;
            ctx.strokeRect(v.x, v.y, v.w, v.h);
          }
        } else {
          ctx.strokeStyle = color;
          ctx.strokeRect(v.x, v.y, v.w, v.h);
        }
        break;
      }
      case 'textbox':
      default: {
        ctx.fillStyle = color;
        ctx.font = `${12 * this.zoom()}px sans-serif`;
        (a.text ?? '').split('\n').forEach((line, i) => {
          const v = this.toView(view, { x: a.x, y: a.y, w: 0, h: 0 });
          ctx.fillText(line, v.x, v.y + 12 * this.zoom() + i * 14 * this.zoom());
        });
        break;
      }
    }
    ctx.restore();
  }

  private strokeInk(ctx: CanvasRenderingContext2D, view: PageView, points: { x: number; y: number }[], color = '#d32f2f'): void {
    if (points.length < 2) {
      return;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    points.forEach((p, i) => {
      const [x, y] = view.viewport.convertToViewportPoint(p.x, p.y);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
  }

  // ---- page operations ---------------------------------------------------------

  async insertPage(): Promise<void> {
    if (this.editDoc) {
      await this.pushHistory(true);
      this.studio.insertBlankPage(this.editDoc, this.currentPage());
      await this.afterOp();
    }
  }

  async deletePage(): Promise<void> {
    if (this.editDoc && this.pageCount() > 1) {
      await this.pushHistory(true);
      const deleted = this.currentPage() - 1;
      this.studio.deletePage(this.editDoc, deleted);
      // marks and fields sit on page indexes — drop the deleted page's and
      // shift every later one down
      this.annots.update((list) => list.filter((a) => a.page !== deleted).map((a) => (a.page > deleted ? { ...a, page: a.page - 1 } : a)));
      this.fields.update((list) => list.filter((f) => f.page !== deleted).map((f) => (f.page > deleted ? { ...f, page: f.page - 1 } : f)));
      await this.afterOp();
    }
  }

  async movePage(from: number, to: number): Promise<void> {
    if (this.editDoc && from !== to && !Number.isNaN(from)) {
      await this.pushHistory(true);
      this.studio.movePage(this.editDoc, from, to);
      await this.afterOp();
      this.goToPage(to + 1);
    }
  }

  async rotatePage(): Promise<void> {
    if (this.editDoc) {
      await this.pushHistory(true);
      await this.studio.rotatePage(this.editDoc, this.currentPage() - 1);
      await this.afterOp();
    }
  }

  private async cropPage(page: number, rect: PdfRect): Promise<void> {
    if (this.editDoc) {
      await this.pushHistory(true);
      this.studio.cropPage(this.editDoc, page, rect);
      await this.afterOp();
    }
  }

  replacePageAsk(): void {
    this.pendingReplacePage = this.currentPage() - 1;
    this.pdfPickEl?.nativeElement.click();
  }

  async onPdfPicked(e: Event): Promise<void> {
    const file = (e.target as HTMLInputElement).files?.[0];
    (e.target as HTMLInputElement).value = '';
    if (!file || !this.editDoc) {
      return;
    }
    await this.pushHistory(true);
    await this.studio.replacePage(this.editDoc, this.pendingReplacePage, new Uint8Array(await file.arrayBuffer()));
    await this.afterOp();
  }

  async extractPage(): Promise<void> {
    if (!this.editDoc) {
      return;
    }
    const bytes = await this.studio.extractPage(this.editDoc, this.currentPage() - 1);
    const name = this.nodeName.replace(/\.pdf$/i, '');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: 'application/pdf' }));
    a.download = `${name}-p${this.currentPage()}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private async afterOp(): Promise<void> {
    if (!this.editDoc) {
      return;
    }
    const bytes = await this.editDoc.save();
    await this.loadViewer(bytes);
    this.dirty.set(true);
  }

  // ---- images & signature ---------------------------------------------------------

  async onImagePicked(e: Event): Promise<void> {
    const file = (e.target as HTMLInputElement).files?.[0];
    (e.target as HTMLInputElement).value = '';
    const pending = this.pendingImageRect;
    this.pendingImageRect = null;
    if (!file || !pending || !file.type.startsWith('image/')) {
      return;
    }
    const kind = file.type.includes('png') ? 'png' : 'jpg';
    const bytes = new Uint8Array(await file.arrayBuffer());
    void this.pushHistory();
    if (pending.replace) {
      this.addAnnot({ id: annotId(), page: pending.page, kind: 'cover', ...pending.rect, text: '', color: '#ffffff' }, false);
    }
    this.addAnnot({ id: annotId(), page: pending.page, kind: 'image', ...pending.rect, image: { bytes, kind } }, false);
    this.tool.set('select');
  }

  openSignPad(): void {
    this.signPadOpen.set(true);
    setTimeout(() => this.initSignPad());
  }

  private initSignPad(): void {
    const canvas = this.signPadEl?.nativeElement;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    let drawing = false;
    canvas.onpointerdown = (e) => {
      drawing = true;
      ctx.beginPath();
      ctx.moveTo(e.offsetX, e.offsetY);
      canvas.setPointerCapture(e.pointerId);
    };
    canvas.onpointermove = (e) => {
      if (drawing) {
        ctx.lineTo(e.offsetX, e.offsetY);
        ctx.stroke();
      }
    };
    canvas.onpointerup = () => (drawing = false);
  }

  clearSignPad(): void {
    const canvas = this.signPadEl?.nativeElement;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  }

  async confirmSignPad(): Promise<void> {
    const canvas = this.signPadEl?.nativeElement;
    if (!canvas) {
      return;
    }
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob) {
      this.signatureImage = { bytes: new Uint8Array(await blob.arrayBuffer()), w: canvas.width, h: canvas.height };
    }
    this.signPadOpen.set(false);
    this.tool.set('sign');
  }

  cancelSignPad(): void {
    this.signPadOpen.set(false);
    this.tool.set('select');
  }

  // ---- search ------------------------------------------------------------------

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (!this.searchOpen) {
      this.searchHits.set([]);
      this.pages.forEach((p) => this.drawOverlay(p));
    }
  }

  async runSearch(): Promise<void> {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term || !this.viewDoc) {
      this.searchHits.set([]);
      return;
    }
    const hits: { page: number; rect: PdfRect }[] = [];
    for (let i = 0; i < this.viewDoc.numPages; i++) {
      const page = await this.viewDoc.getPage(i + 1);
      const content = await page.getTextContent();
      for (const item of content.items) {
        if (!('str' in item) || !item.str.toLowerCase().includes(term)) {
          continue;
        }
        // item transforms are in PDF user space already — tx[5] is the
        // baseline, so the box reaches up through cap height and a little
        // below for descenders
        const tx = (item as any).transform;
        const w = (item as any).width;
        const h = (item as any).height;
        hits.push({ page: i, rect: { x: tx[4], y: tx[5] - h * 0.2, w, h: h * 1.2 } });
      }
      page.cleanup();
    }
    this.searchHits.set(hits);
    this.searchIndex.set(0);
    this.pages.forEach((p) => this.drawOverlay(p));
    if (hits.length) {
      this.goToPage(hits[0].page + 1);
    }
  }

  nextHit(d: number): void {
    const hits = this.searchHits();
    if (!hits.length) {
      return;
    }
    const i = (this.searchIndex() + d + hits.length) % hits.length;
    this.searchIndex.set(i);
    this.goToPage(hits[i].page + 1);
    this.pages.forEach((p) => this.drawOverlay(p));
  }

  private drawSearchHits(ctx: CanvasRenderingContext2D, view: PageView): void {
    const hits = this.searchHits().filter((h) => h.page === view.index);
    hits.forEach((h) => {
      const r = this.toView(view, h.rect);
      const on = this.searchHits().indexOf(h) === this.searchIndex();
      ctx.fillStyle = on ? 'rgba(255,152,0,0.45)' : 'rgba(255,235,59,0.4)';
      ctx.fillRect(r.x, r.y, r.w, r.h);
    });
  }

  // ---- save ---------------------------------------------------------------------

  async save(major: boolean): Promise<void> {
    // saving rewrites the whole file, and a digital signature does not survive that
    const intact = (this.signatures() ?? []).filter((s) => s.intact).length;
    if (intact > 0) {
      const go = await firstValueFrom(
        this.dialog
          .open(PromptDialogComponent, {
            data: { title: this.translate.instant('DMS.PDFTOOLS.DS_SAVE_BREAKS', { p0: intact }), fields: [], confirm: 'DMS.PDFTOOLS.DS_SAVE_ANYWAY' }
          })
          .afterClosed()
      );
      if (!go) {
        return;
      }
    }
    if (await this.persist(major)) {
      this.loadSignatures();
    }
  }

  /** Burns the marks in and saves a new version; true when it was saved. */
  private async persist(major: boolean): Promise<boolean> {
    if (!this.editDoc || this.busy()) {
      return false;
    }
    this.busy.set(true);
    try {
      // Burn into a copy so a failed upload leaves the working document clean
      const out = await this.studio.loadDoc(await this.editDoc.save());
      await this.studio.burn(out, this.annots(), this.fields());

      // A box drawn over text leaves the text in the file. A page with a redaction is
      // replaced by a picture of itself, so what is under the box is gone, not hidden.
      const redacted = new Set(
        this.annots()
          .filter((a) => a.kind === 'redact')
          .map((a) => a.page)
      );
      const burned = await this.studio.saveBytes(out);
      const bytes = redacted.size ? await this.redaction.flatten(burned, redacted) : burned;
      await new Promise<void>((resolve, reject) => {
        this.studio.saveAsNewVersion(this.nodeId, this.nodeName, bytes, major, this.translate.instant('DMS.PDFTOOLS.SAVE_COMMENT')).subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
      });
      this.originalBytes = bytes;
      this.annots.set([]);
      this.fields.set([]);
      this.clearHistory();
      this.dirty.set(false);
      this.saved.emit();
      this.notification.showInfo(this.translate.instant('DMS.PDFTOOLS.SAVED'));
      if (redacted.size) {
        this.notification.showWarning(this.translate.instant('DMS.PDFTOOLS.REDACT_VERSIONS'));
        void this.reindexAfterRedaction(bytes);
      }
      return true;
    } catch {
      this.notification.showError(this.translate.instant('DMS.PDFTOOLS.SAVE_FAILED'));
      return false;
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * The search index still holds the words that were just covered. Drop them, then read the new
   * pages again so the document stays findable by what is left. If reading fails the index stays
   * empty for this document — losing a search hit is better than keeping the personal data in it.
   */
  private async reindexAfterRedaction(bytes: Uint8Array): Promise<void> {
    try {
      await firstValueFrom(this.ocr.removeFromIndex(this.nodeId));
    } catch {
      this.notification.showWarning(this.translate.instant('DMS.PDFTOOLS.REDACT_INDEX_FAILED'));
      return;
    }

    try {
      const file = new File([bytes.slice()], this.nodeName, { type: 'application/pdf' });
      await firstValueFrom(this.pipeline.readAndIndex(file, this.nodeId, this.nodeName, () => undefined));
    } catch {
      this.notification.showWarning(this.translate.instant('DMS.PDFTOOLS.REDACT_REINDEX_FAILED'));
    }
  }

  /** Throws every edit away — back to the version on the server. */
  async discard(): Promise<void> {
    if (!this.originalBytes) {
      return;
    }
    this.busy.set(true);
    try {
      this.editDoc = await this.studio.loadDoc(this.originalBytes);
      this.annots.set([]);
      this.fields.set([]);
      this.clearHistory();
      await this.loadViewer(this.originalBytes);
      this.dirty.set(false);
    } finally {
      this.busy.set(false);
    }
  }
}

/** A rectangle from two PDF-space corners, normalised. */
function normRect(x1: number, y1: number, x2: number, y2: number): PdfRect {
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
}

/** Smallest box containing all rects. */
function unionRects(rects: PdfRect[]): PdfRect {
  const x1 = Math.min(...rects.map((r) => r.x));
  const y1 = Math.min(...rects.map((r) => r.y));
  const x2 = Math.max(...rects.map((r) => r.x + r.w));
  const y2 = Math.max(...rects.map((r) => r.y + r.h));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/** Standard-size field rect dropped on a plain click, centred on the point. */
function defaultFieldRect(tool: ToolId, x: number, y: number): PdfRect {
  const size = tool === 'field-check' || tool === 'field-radio' ? { w: 16, h: 16 } : { w: 170, h: 24 };
  return { x: x - size.w / 2, y: y - size.h / 2, w: size.w, h: size.h };
}

/**
 * The clickable box of a mark in PDF space. Marks that only carry an anchor
 * point (notes, ink, text anchored at its top edge) get a computed box.
 */
function annotBounds(a: PdfAnnot): PdfRect {
  if (a.kind === 'ink' && a.points?.length) {
    const xs = a.points.map((p) => p.x);
    const ys = a.points.map((p) => p.y);
    const x1 = Math.min(...xs);
    const y1 = Math.min(...ys);
    return { x: x1, y: y1, w: Math.max(...xs) - x1, h: Math.max(...ys) - y1 };
  }
  if (a.kind === 'note') {
    return { x: a.x, y: a.y, w: 12, h: 12 };
  }
  if (a.kind === 'textbox' || (a.kind === 'cover' && a.text)) {
    // text is anchored at its top edge and flows downward on screen
    return { x: a.x, y: a.y - (a.h || 16), w: a.w || 80, h: a.h || 16 };
  }
  return { x: a.x, y: a.y, w: a.w, h: a.h };
}

/** Returns the mark shifted by a PDF-space delta (rects and ink points included). */
function offsetAnnot(a: PdfAnnot, dx: number, dy: number): PdfAnnot {
  return {
    ...a,
    x: a.x + dx,
    y: a.y + dy,
    rects: a.rects?.map((r) => ({ ...r, x: r.x + dx, y: r.y + dy })),
    points: a.points?.map((p) => ({ x: p.x + dx, y: p.y + dy }))
  };
}
